import React, { useState, useEffect } from 'react';
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../../services/authService';

const PendingApprovalsWidget = () => {
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedOverride, setSelectedOverride] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    loadPendingApprovals();
    // Recarregar a cada 30 segundos
    const interval = setInterval(loadPendingApprovals, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadPendingApprovals = async () => {
    try {
      const response = await api.get('/api/credit-policy/override/pending');
      const data = response.data;
      setPendingApprovals(data.data || []);
    } catch (error) {
      console.error('Erro ao carregar aprovações:', error);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (overrideId) => {
    try {
      await api.put(`/api/credit-policy/override/${overrideId}/approve`, {
        approval_role: 'director'
      });
      toast.success('Exceção aprovada com sucesso!');
      loadPendingApprovals();
    } catch (error) {
      console.error('Erro ao aprovar:', error);
      const message = error.response?.data?.error || 'Erro ao aprovar exceção';
      toast.error(message);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Por favor, informe o motivo da rejeição');
      return;
    }

    try {
      await api.put(`/api/credit-policy/override/${selectedOverride.id}/reject`, {
        rejection_reason: rejectionReason,
        approval_role: 'director'
      });
      toast.success('Exceção rejeitada');
      setShowRejectModal(false);
      setRejectionReason('');
      setSelectedOverride(null);
      loadPendingApprovals();
    } catch (error) {
      console.error('Erro ao rejeitar:', error);
      const message = error.response?.data?.error || 'Erro ao rejeitar exceção';
      toast.error(message);
    }
  };

  const openRejectModal = (override) => {
    setSelectedOverride(override);
    setShowRejectModal(true);
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <div className="text-center text-gray-600 dark:text-gray-400">
          Carregando...
        </div>
      </div>
    );
  }

  if (pendingApprovals.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <ClockIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Solicitações de Exceção Pendentes
          </h3>
        </div>
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <CheckCircleIcon className="w-12 h-12 mx-auto mb-2 text-green-500" />
          <p>Nenhuma solicitação pendente</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <ClockIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Solicitações de Exceção Pendentes
          </h3>
        </div>
        <span className="px-3 py-1 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded-full text-sm font-bold">
          {pendingApprovals.length}
        </span>
      </div>

      <div className="space-y-4">
        {pendingApprovals.map((approval) => (
          <div
            key={approval.id}
            className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
          >
            {/* Cliente */}
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white">
                  {approval.client_name}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {approval.client_document}
                </p>
              </div>
              <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 rounded text-xs font-medium">
                {approval.original_risk_level ? approval.original_risk_level.toUpperCase() : 'N/A'}
              </span>
            </div>

            {/* Solicitante */}
            <div className="flex items-center gap-2 mb-3 text-sm text-gray-600 dark:text-gray-400">
              <UserIcon className="w-4 h-4" />
              <span>Solicitado por:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {approval.requested_by_name || 'Desconhecido'}
              </span>
              <span className="text-xs">
                ({new Date(approval.created_at).toLocaleDateString('pt-BR')})
              </span>
            </div>

            {/* Mudanças */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 mb-3 space-y-2">
              <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Mudanças Solicitadas:
              </h5>

              {/* Caução */}
              {approval.original_deposit_months !== approval.overridden_deposit_months && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Caução:</span>
                  <span className="font-mono text-gray-900 dark:text-white">
                    {approval.original_deposit_months || 'N/A'} mês{approval.original_deposit_months > 1 ? 'es' : ''}
                  </span>
                  <ArrowRightIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="font-mono font-semibold text-blue-600 dark:text-blue-400">
                    {approval.overridden_deposit_months || 'N/A'} mês{approval.overridden_deposit_months > 1 ? 'es' : ''}
                  </span>
                </div>
              )}

              {/* Fiador */}
              {approval.original_requires_guarantor !== approval.overridden_requires_guarantor && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Fiador:</span>
                  <span className="font-mono text-gray-900 dark:text-white">
                    {approval.original_requires_guarantor ? 'Obrigatório' : 'Não necessário'}
                  </span>
                  <ArrowRightIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="font-mono font-semibold text-blue-600 dark:text-blue-400">
                    {approval.overridden_requires_guarantor ? 'Obrigatório' : 'Dispensado'}
                  </span>
                </div>
              )}
            </div>

            {/* Justificativa */}
            <div className="mb-4">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Justificativa:
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                "{approval.override_reason}"
              </p>
            </div>

            {/* Ações */}
            <div className="flex gap-3">
              <button
                onClick={() => handleApprove(approval.id)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <CheckCircleIcon className="w-5 h-5" />
                Aprovar
              </button>
              <button
                onClick={() => openRejectModal(approval)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <XCircleIcon className="w-5 h-5" />
                Rejeitar
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal de Rejeição */}
      {showRejectModal && selectedOverride && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Rejeitar Solicitação
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Cliente: <span className="font-semibold">{selectedOverride.client_name}</span>
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Por favor, informe o motivo da rejeição:
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              rows={4}
              placeholder="Ex: Risco muito alto para flexibilização, política não permite, etc."
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedOverride(null);
                  setRejectionReason('');
                }}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleReject}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Confirmar Rejeição
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingApprovalsWidget;
